import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import backgroundSvgDark from "../../../assets/svg/AuthBgLight.svg";
import zookeeperLogo from "../../../assets/svg/ZookeeperLogoWhite.svg";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import Loader from "../../../components/loader/Loader";
import { ThemeContext } from "../../../contexts/ThemeContext";
import { fetchClinicList } from "../../../enums/queryFunction/Fetch";
import { CLINICS } from "../../../enums/QueryKeys";
import { LOGIN, SIGN_UP, TERMS } from "../../../enums/UrlPaths";
import LegalStepper from "./components/steps/LegalStepper";
import PersonalStepper from "./components/steps/PersonalStepper";
import { useSearchParams } from "react-router-dom";

function CardWrapper({ children }) {
  return (
    <Grid item>
      <Card
        sx={{
          width: 280,
          textAlign: "center",
          background: "transparent",
          borderRadius: 0,
          color: "white",
          boxShadow: "none",
        }}
      >
        {children}
      </Card>
    </Grid>
  );
}

export default function SignUpPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accountType, setAccountType] = useState(
    searchParams.get("accountType") || null
  );

  function handleSetAccountType(type) {
    const newParams = new URLSearchParams(searchParams);

    if (type) {
      newParams.set("accountType", type);
    } else {
      newParams.delete("accountType");
      newParams.delete("step")
    }

    setSearchParams(newParams); // Updates the URL without reloading
    setAccountType(type);
  }

  const { isLoading } = useQuery({
    queryKey: [CLINICS],
    queryFn: fetchClinicList,
    staleTime: Infinity,
  });

  const { setTheme } = React.useContext(ThemeContext);
  const rightSide = 5;
  const leftSideSize = 12 - rightSide;

  React.useEffect(() => {
    setTheme("dark");
  }, []);

  React.useEffect(() => {
    setAccountType(searchParams.get("accountType"));
  }, [searchParams]);

  if (isLoading) return <Loader />;

  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        backgroundImage: `url(${backgroundSvgDark})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Left Side - Branding (Hidden on smaller screens) */}
      {/* Make this into a component */}
      <Grid
        item
        flexDirection="column"
        justifyContent="space-between"
        display={{ xs: "none", md: "flex" }}
        md={leftSideSize - 2}
        lg={leftSideSize}
      >
        <Grid
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <img src={zookeeperLogo} width={"80%"} />
          <Typography variant="h3" textAlign={"center"} letterSpacing={8}>
            A new way to care
          </Typography>
          <Typography variant="body1" color="white" mt={3}>
            {t("PowerdByKilobyte")}
          </Typography>
        </Grid>
      </Grid>

      {/* Right Side - Sign Up */}
      <Grid
        item
        xs={12} // Take full width on xs
        md={rightSide + 2} // Keep right side layout on md screens
        lg={rightSide} // Keep right side layout on md screens
        sx={{
          bgcolor: "#001f3fbb",
          display: "flex",
          minHeighteight: "100vh",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          p: 5,
          position: "relative",
        }}
      >
        {!accountType ? (
          <Stack
            minHeight={"80vh"}
            justifyContent={"space-around"}
            sx={{ textAlign: "center", mt: 4, width: "100%" }}
          >
            {/* Make this into a component */}

            <Box>
              <Typography variant="h4" fontWeight={600} gutterBottom>
                {t("ChooseAccountType")}
              </Typography>
              <Typography variant="body2" color="gray" mb={2}>
                {t("SelectAccountTypeForYou")}
              </Typography>
            </Box>

            {/* Account Type Cards */}
            <Grid container justifyContent="center">
              {/* Personal Account */}
              <CardWrapper>
                <CardActionArea
                  onClick={() => handleSetAccountType("personal")}
                  data-testid="auth.signup.personal-account-card"
                >
                  <CardContent>
                    <PersonIcon sx={{ fontSize: 50, mb: 1 }} />
                    <Typography variant="h5" fontWeight={500}>
                      {t("PersonalAccount")}
                    </Typography>
                    <Typography variant="body2">
                      {t("ForIndividuals")}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </CardWrapper>
              <Divider orientation="vertical" variant="middle" flexItem />
              {/* Business Account */}
              <CardWrapper>
                <CardActionArea
                  onClick={() => handleSetAccountType("business")}
                  data-testid="auth.signup.business-account-card"
                >
                  <CardContent>
                    <BusinessIcon sx={{ fontSize: 50, mb: 1 }} />
                    <Typography variant="h5" fontWeight={500}>
                      {t("BussinessAccount")}
                    </Typography>
                    <Typography variant="body2">
                      {t("ForCompaniesAndProfessionals")}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </CardWrapper>
            </Grid>

            {/* Terms and Login Links */}
            {/* Make this into a component */}

            <Stack
              sx={{ mt: 4 }}
              spacing={2}
              justifyContent={"space-around"}
              alignItems={"center"}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {t("AlreadyHaveAnAccount?")}{" "}
                <Link to={LOGIN} style={{ color: "#1976d2", fontWeight: 500 }}>
                  {t("LogIn")}
                </Link>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <Link to={TERMS} style={{ color: "#1976d2", fontWeight: 500 }}>
                  {t("TOS")}
                </Link>
              </Typography>
              <Box>
                <LanguageSwitcher />
              </Box>
            </Stack>
          </Stack>
        ) : (
          <Box sx={{ width: "100%", mt: 4 }}>
            {accountType === "personal" ? (
              <PersonalStepper onCancel={() => handleSetAccountType(null)} />
            ) : (
              <LegalStepper onCancel={() => handleSetAccountType(null)} />
            )}
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
